/* svnty-pulse.js - THE PULSE, the conductor of THE SYNC BOUNCE (D6-11, R202, 2026-09-25 20:45).
 * Classic, deferred, no deps, ES5, ASCII. Brief: 03_design/research/sync_bounce_2026-09-25/SYNC_BOUNCE_BRIEF_2026-09-25.md.
 *
 * ONE spring for every glyph of a pair (the card's bag and the header's bag; the card's heart and the header's heart;
 * the menu bar's marks with the header's). Every Animation of one change is pinned to ONE document-timeline startTime
 * (the stamp), so the glyphs of a pair share one phase on every frame, even one that learns a task later.
 *   THE POP (a count goes up, a heart fills): the impulse response of a damped spring, response 0.42 s, dampingFraction
 *     0.62, drawn as translateY(LIFT d) scale(1 + AMP d): peak 1 + AMP at about 77 ms, one dip, rest at 0.5 s.
 *   THE SETTLE (a count goes down, a heart empties): the same response critically damped, a quiet dip to 0.96.
 *   THE FILL (200 ms) and THE TICK (150 ms) start on the same stamp.
 * WAAPI keyframe arrays (iOS 13.1+), compositor-only (transform, opacity). Reduced motion: the fill and the tick as
 * fades, no transform. The end states live in CSS, so the page stays right with no WAAPI at all.
 *
 * window.SVNTY.pulse.play(host, kind, { up, fill: 'in' | 'out' | null, tick })
 *   host: an element carrying one glyph (THE COUNTED ICON, or a <svnty-wishlist-toggle>); kind: 'bag' | 'heart' | ...
 *   THE GUARD: one play per host per stamp id.
 */
(function () {
  'use strict';
  var SV = window.SVNTY = window.SVNTY || {};
  if (SV.pulse) return;

  /* THE AMPLITUDE (it may be pulled to 0.08 after an iPhone check): the one constant of THE POP's size */
  var AMP = 0.10;

  var SPR = {
    pop: { response: 0.42, damping: 0.62, ms: 520, amp: AMP, lift: -1 },
    settle: { response: 0.42, damping: 1.00, ms: 520, amp: -0.04, lift: 0 }
  };
  var N = 33, FILL_MS = 200, TICK_MS = 150, WINDOW = 250;
  var EASE = 'cubic-bezier(0.2, 0, 0, 1)', TICK_EASE = 'cubic-bezier(0.05, 0.7, 0.1, 1)';
  var GLYPH = '.svnty-ci__glyph, .svnty-heart__glyph';
  var OUTLINE = '.svnty-ci__outline, .svnty-heart__glyph > .svnty-icon--heart';
  var SOLID = '.svnty-ci__solid, .svnty-heart__glyph > .svnty-icon--heart-filled';

  /* d(t), normalised to a peak of 1: underdamped e^(-zeta w t) sin(w_d t), critically damped t e^(-w t); w = 2 pi / response */
  function impulse(s) {
    var w = 2 * Math.PI / s.response, z = s.damping, T = s.ms / 1000, pts = [], mx = 0, i, t, v;
    var wd = w * Math.sqrt(Math.max(0, 1 - z * z));
    function d(t) { return z < 1 ? Math.exp(-z * w * t) * Math.sin(wd * t) : t * Math.exp(-w * t); }
    for (i = 0; i < 2000; i++) { v = Math.abs(d(T * i / 1999)); if (v > mx) mx = v; }
    for (i = 0; i < N; i++) { t = T * i / (N - 1); pts.push(i === N - 1 ? 0 : d(t) / mx); }
    return pts;
  }
  function frames(s) {
    return impulse(s).map(function (d, i) {
      return { offset: i / (N - 1), transform: 'translateY(' + (s.lift * d).toFixed(3) + 'px) scale(' + (1 + s.amp * d).toFixed(4) + ')' };
    });
  }
  /* for CSS use on iOS 17.2+ only; the shipped path is the WAAPI array */
  function linearEasing(s) { return 'linear(' + impulse(s).map(function (d) { return +d.toFixed(4); }).join(', ') + ')'; }
  var FR = { pop: frames(SPR.pop), settle: frames(SPR.settle) };
  var FADE_IN = [{ opacity: 0 }, { opacity: 1 }], FADE_OUT = [{ opacity: 1 }, { opacity: 0 }];

  var CAN = typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';
  var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var stamps = {}, seq = 0;

  function now() {
    var tl = document.timeline && document.timeline.currentTime != null ? Number(document.timeline.currentTime) : null;
    return tl === null ? (window.performance ? performance.now() : Date.now()) : tl;
  }
  function pin(a, t0) { try { a.startTime = t0; } catch (e) {} return a; }
  /* the FIRST glyph of a change opens the stamp; every glyph of the same kind and direction within WINDOW ms joins it.
     The stamp's time is the FIRST FRAME after the change: it opens on the timeline's current time and is moved, with
     every Animation already pinned to it, to the next frame's time. So the spring starts at its start however long the
     task that caused it runs (an add's answer repaints the toast and the header in the same task), and a glyph that
     joins later inherits the phase instead of starting over. */
  function stamp(kind, up) {
    var s = stamps[kind], t = now();
    if (s && t - s.t0 < WINDOW && s.up === up) return s;
    s = stamps[kind] = { t0: t, up: up, id: ++seq, anims: [] };
    if (window.requestAnimationFrame) requestAnimationFrame(function () {
      s.t0 = now();
      for (var i = 0; i < s.anims.length; i++) if (s.anims[i].playState !== 'idle') pin(s.anims[i], s.t0);   /* a cancelled one stays cancelled */
    });
    return s;
  }
  function run(el, kf, ms, easing, s, list) {
    if (!el) return;
    var a = pin(el.animate(kf, { duration: ms, easing: easing }), s.t0);
    list.push(a); s.anims.push(a);
  }

  function play(host, kind, change) {
    change = change || {};
    var up = !!change.up, s = stamp(kind, up);
    if (!host || !CAN || host.__pulseId === s.id) return s;
    host.__pulseId = s.id;
    var prev = host.__pulseAnims, list = [], rm = !!(mq && mq.matches), i;
    if (prev) for (i = 0; i < prev.length; i++) { try { prev[i].cancel(); } catch (e) {} }
    try {
      var g = host.querySelector(GLYPH), out = host.querySelector(OUTLINE), sol = host.querySelector(SOLID);
      /* the badge layout keeps the outline and puts the number outside: no fill to play */
      var badge = document.documentElement.getAttribute('data-svnty-count') === 'badge' && !!host.querySelector('.svnty-ci__badge');
      if (change.fill && out && sol && !badge) {
        var inn = change.fill === 'in';
        run(sol, inn ? FADE_IN : FADE_OUT, FILL_MS, EASE, s, list);
        run(out, inn ? FADE_OUT : FADE_IN, FILL_MS, EASE, s, list);
      }
      if (change.tick) {
        var dy = up ? -3 : 3, tk = rm ? FADE_IN : [{ opacity: 0, transform: 'translateY(' + dy + 'px)' }, { opacity: 1, transform: 'none' }];
        var digs = host.querySelectorAll(badge ? '.svnty-ci__badge' : '.svnty-ci__digits');
        for (i = 0; i < digs.length; i++) if (digs[i].textContent) run(digs[i], tk, TICK_MS, TICK_EASE, s, list);
      }
      if (!rm && g) run(g, up ? FR.pop : FR.settle, SPR.pop.ms, 'linear', s, list);
    } catch (e) {}
    host.__pulseAnims = list;
    return s;
  }

  SV.pulse = { play: play, stamp: stamp, SPR: SPR, frames: FR, linearEasing: linearEasing, AMP: AMP };
})();
